import { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button, Table, Tag, Typography, Form, Row, Col, Select, Space, Card, theme, Flex, Tooltip } from 'antd';
import { conferenceOnceApi } from '@services/Conference';
import { fetchLaborFeePaymentManageList } from '@services/Finance/FinanceLaborFee.service.js';
import { filterFormItemLayout, filterFormGridLayout, filterFormItemStyle, computeButtonColLayout } from '@utils/property';
import { localStorageGet } from '@core/cookie';
import { convertDateTimeToLocalText } from '../../../utils/utils';
import PageHeader from '@components/PageHeader/PageHeader';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import { useFilterPanelVisible } from '@/hooks/useFilterPanelVisible';
const { Text } = Typography;
export default function FinanceLaborFeePaymentBatches() {
    const navigate = useNavigate();
    const params = useParams();
    const { filterToggleProps, FilterPanelSlot } = useFilterPanelVisible();

    // 接收项目类型-项目ID路由参数
    const projectCodeAndId = params.project || '';
    let projectCode = '';
    let projectId = 0;
    if (!!projectCodeAndId && /[a-z]+-\d+/i.test(projectCodeAndId)) {
        [projectCode, projectId] = projectCodeAndId.split('-');
        projectCode = projectCode.toUpperCase() || '';
        projectId = parseInt(projectId, 10) || 0;
    }

    const [conference, setConference] = useState(null);

    const [form] = Form.useForm();

    const [datas, setDatas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchParams, setSearchParams] = useState({});

    const { token } = theme.useToken();

    // fixed-scroll 三件套：表格高度自适应 + 横向滚动守卫 + 纵向阴影
    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableDeductSelectors = useMemo(() => ['.main-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, { threshold: 6, edgeHoldMs: 220, watchValue: scrollContainerVersion });
    useVerticalScrollShadow(tableScrollContainerRef, { threshold: 2, watchValue: scrollContainerVersion });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;
        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };
        resolveScrollContainer();
        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    useEffect(() => {
        document.title = '劳务费付款管理-财务中心';
    }, []);

    // 是否拥有财务劳务费付款人员权限
    const isLaborFeePayment = useMemo(() => {
        const roleList = localStorageGet('roleList') ? JSON.parse(localStorageGet('roleList')) : [];
        if (roleList && roleList.length) {
            return roleList.some(item => item.name === '财务劳务费付款人员');
        }
        return false;
    }, []);

    // 是否拥有财务劳务费管理员权限
    const isLaborFeeManager = useMemo(() => {
        const roleList = localStorageGet('roleList') ? JSON.parse(localStorageGet('roleList')) : [];
        if (roleList && roleList.length) {
            return roleList.some(item => item.name === '财务劳务费管理员');
        }
        return false;
    }, []);

    const approvalOptions = [
        { value: 0, label: '待审批' },
        { value: 1, label: '同意' },
    ];

    const paymentOptions = [
        { value: 0, label: '待付款' },
        { value: 1, label: '付款中' },
        { value: 2, label: '全部付款' },
    ];

    const columns = [
        {
            title: '付款申请时间',
            width: 200,
            dataIndex: 'batchRequestAt',
            render: (_, row) => convertDateTimeToLocalText(row.batchRequestAt),
        },
        {
            title: '实发劳务费(元)',
            dataIndex: 'laborFeeAmount',
            width: 150,
            render: (_, row) => (
                <>
                    <div>&yen;{formatCurrency(row.laborFeeAmount)}</div>
                </>
            ),
        },
        {
            title: '相关税费(元)',
            dataIndex: 'individualTaxAmount',
            width: 150,
            render: (_, row) => (
                <>
                    <div>&yen;{formatCurrency(row.individualTaxAmount)}</div>
                </>
            ),
        },
        {
            title: '含税劳务费(元)',
            dataIndex: 'laborFee',
            width: 150,
            render: (_, row) => <>{<div>&yen;{formatCurrency(parseFloat(row.laborFeeAmount) + parseFloat(row.individualTaxAmount))}</div>}</>,
        },
        {
            title: '申请人',
            dataIndex: 'batchRequestBy',
            width: 120,
            render: (_, row) => <>{row.batchRequestBy ? <div>{row.batchRequestBy}</div> : <div>-</div>}</>,
        },
        {
            title: '审核状态',
            dataIndex: 'examinedStatus',
            key: 'examinedStatus',
            width: 120,
            render: (_, row) => (
                <>
                    {row.examinedStatus === 0 && <Text type="warning">待审批</Text>}
                    {row.examinedStatus === 1 && <Text type="success">同意</Text>}
                </>
            ),
        },
        {
            title: '提交单据',
            dataIndex: 'receiptSubmission',
            key: 'receiptSubmission',
            width: 120,
            render: (_, row) => (
                <>
                    {row.receiptSubmission === 0 && <Text type="warning">未提交</Text>}
                    {row.receiptSubmission === 1 && <Text type="success">已提交</Text>}
                </>
            ),
        },
        {
            title: '付款状态',
            dataIndex: 'payedStatus',
            key: 'payedStatus',
            width: 120,
            render: (_, row) => (
                <>
                    {row.payedStatus === 0 && <Text type="warning">待付款</Text>}
                    {row.payedStatus === 1 && <Text type="success">付款中</Text>}
                    {row.payedStatus === 2 && <Text type="success">全部付款</Text>}
                </>
            ),
        },
        {
            title: '付款进度',
            dataIndex: 'payedCount',
            key: 'payedCount',
            width: 120,
            render: (_, row) => (
                <>
                    {row.payedCount}/{row.batchCount}人
                </>
            ),
        },
        {
            title: '操作',
            dataIndex: 'state',
            key: 'state',
            width: 200,
            render: (_, row) => (
                <>
                    {(isLaborFeePayment || isLaborFeeManager) && (
                        <Button size="small" type="link" onClick={() => gotoFinanceLaborFeePayment(row.batchNo)}>
                            查看明细
                        </Button>
                    )}
                </>
            ),
        },
    ];

    // 处理金额
    const formatCurrency = value => {
        let result = '';

        const f = parseFloat(value) || 0.0;
        const parts = f.toFixed(2).split('.');
        result = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,') + (parts[1] ? '.' + parts[1] : '');

        return result;
    };

    const onFinish = values => {
        setSearchParams({ ...values });
    };

    // 重置
    const onReset = () => {
        form.resetFields();
        setSearchParams({});
    };

    const getLaborFeePaymentManageList = async () => {
        setLoading(true);
        const newValues = {
            ...form.getFieldsValue(),
        };
        const res = await fetchLaborFeePaymentManageList({
            ...newValues,
            projectCode: projectCode,
            projectId: projectId,
        });
        if (res.status_code === 200) {
            setDatas(res.data || []);
        }
        setLoading(false);
    };

    const gotoFinanceLaborFeePayment = batchNo => {
        navigate(`/finance/laborfee/projects/${projectCode}-${projectId}/batches/${batchNo}`);
    };

    // 获取会议信息
    const getConferenceInfo = async projectId => {
        const res = await conferenceOnceApi({ id: projectId });
        if (res.status_code === 200 && res.data) {
            setConference(res.data);
        }
    };

    useEffect(() => {
        if (!!projectId) {
            getConferenceInfo(projectId);
        }
    }, []);

    useEffect(() => {
        getLaborFeePaymentManageList();
    }, [searchParams, projectId]);

    return (
        <div className="page-container list-page-container">
            <PageHeader breadcrumb={[]} {...filterToggleProps}>
                <Link to={`/finance/laborfee/projects`} className="back">
                    <i className="bi bi-chevron-left"></i>
                </Link>
                <span className="current-title">劳务费付款管理</span>
                <span className="current-subtitle">{conference?.name}</span>
            </PageHeader>

            <FilterPanelSlot>
                <Card
                    size="small"
                    className="finance-laborfee-batches-filter-panel"
                    style={{
                        marginBottom: token.marginLG,
                        border: `1px solid ${token.colorBorderSecondary}`,
                        background: token.colorFillAlter,
                    }}
                >
                    <Form form={form} name="control-hooks" onFinish={onFinish} {...filterFormItemLayout} autoComplete="off" requiredMark={false}>
                        <Row gutter={[16, 16]}>
                            <Col {...filterFormGridLayout}>
                                <Form.Item name="examinedStatus" label="审核状态" style={filterFormItemStyle}>
                                    <Select placeholder="选择审批状态" allowClear options={approvalOptions} fieldNames={{ value: 'value', label: 'label' }} />
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item name="payedStatus" label="付款状态" style={filterFormItemStyle}>
                                    <Select placeholder="选择付款状态" allowClear options={paymentOptions} fieldNames={{ value: 'value', label: 'label' }} />
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label={<span>&nbsp;</span>} colon={false} style={filterFormItemStyle}>
                                    <Space size="small">
                                        <Button type="primary" icon={<i className="bi bi-search"></i>} htmlType="submit">
                                            检索
                                        </Button>
                                        <Button type="default" onClick={onReset}>
                                            重置
                                        </Button>
                                    </Space>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Card>
            </FilterPanelSlot>

            <div ref={tableRegionRef} className="table-region-container">
                <div className="main-toolbar finance-laborfee-batches-toolbar">
                    <div className="main-tools">
                        <Space></Space>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>
                <div className="list-table-wrap">
                    <Table className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined} columns={columns} dataSource={datas} loading={loading} pagination={null} scroll={{ x: 'max-content', ...(tableScrollY != null ? { y: tableScrollY } : {}) }} />
                </div>
            </div>
        </div>
    );
}
